import { PlayerEnvironment } from "./player_environment";

export class PlayerBrowserSupport {

  static isSupported(){
    return !(
        // Opera mini renders on the server, therefore it can't have a video
        // element, but on Opera Mini version 8 in iOS it renders normally if
        // the user enables Turbo mode instead of Mini mode.
        //
        // Reference:
        // - https://dev.opera.com/articles/opera-mini-content-authoring-guidelines/#javascript
        // - https://dev.opera.com/blog/opera-mini-8-for-ios/
        //
        // Pivotal: #129031907
        PlayerBrowserSupport.isOperaMini() ||

        /** Unsupported Browsers Without Flash **/

        // Firefox version less than 42 without flash doesn't have Media Source
        // Extension available, even though the feature exists since version
        // 25, it is disabled by default and is available to a limited sites
        // such as Netflix and Youtube.
        //
        // Reference:
        // - https://www.mozilla.org/en-US/firefox/42.0/releasenotes/
        // - https://developer.mozilla.org/en-US/docs/Web/API/Media_Source_Extensions_API
        //
        // Pivotal: #131973307
        PlayerBrowserSupport.isFirefoxWithoutMSEandFlash() ||

        // All Firefox on Windows XP without flash doesn't have MSE due to the
        // inexistent of MP4 decoder in it that can be used by the browser.
        //
        // Reference:
        // - https://bugzilla.mozilla.org/show_bug.cgi?id=1119535
        //
        // Pivotal: #131233879
        (!PlayerBrowserSupport.isHavingFlash() && PlayerBrowserSupport.isFirefoxOnWindowsXP()) ||

        // Palemoon is firefox variant, so that it will behave like firefox
        // Palemoon on Windows XP without flash doesn't have MSE due to the
        // inexistent of MP4 decoder in it that can be used by the browser.
        // It proven from plenty where Palemoon on Windows XP contribute on high error rates
        PlayerBrowserSupport.isPalemoonOnWindowsXP() ||

        // Opera version less than 30 without flash doesn't have MSE with
        // H.264, it only has MSE with WebM prior to this version.
        //
        // Reference:
        // - https://dev.opera.com/blog/opera-30/
        //
        // Pivotal: #132760025
        PlayerBrowserSupport.isOperaWithoutMSEH264andFlash() ||

        // On android browser version 2, support 0% of MSE H264 and canPlayType(`application/x-mpegURL`)
        // Pivotal: #133572163
        PlayerBrowserSupport.isMobileAndroidBrowserVersion2X() ||

        // On desktop platform with browser Chrome and version 6 found:
        // - 0% support to mseh264
        // - 0% support to vjs_mpegurl
        // - 90% error rate
        PlayerBrowserSupport.isDesktopChromeBrowserVersion6X() ||

        // Based on current performance for jwplayer: 
        // - error rate > 50% for all videos
        // - error rate without ads > 90%
        // - error rate with ads < 10%
        // 
        // Experiment result for videojs:
        // - `The media could not be loaded, either because the server or network failed or because the format is not supported.`
        // - `HTTP “Content-Type” of “application/vnd.apple.mpegurl” is not supported. Load of media resource http://10.11.9.7:9898/videos/29s-65614/xo-3011-b,300,600,.mp4.m3u8 failed.`
        // - `Firefox Mobile version that support hls is version 44 and greater`
        PlayerBrowserSupport.isUnsupportedFirefoxMobileOnAndroid() ||

        // Error rate IE Mobile on all versions is > 90%
        //  From last 3 days error message found 'No compatible source was found for this media' more than 80% which is can be identified this browser is not supported by videojs
        //  From last 3 days second dominant error message is 'The media could not be loaded, either because the server or network failed or because the format is not supported.'
        PlayerBrowserSupport.isIEMobile() ||

        // Based on investigation on high error rate for VideoJS HTML5 player,
        // Major error rate comes from these browsers:
        // - Chrome
        // - Firefox
        // - Other
        //
        // Further investigation on "Other" browser revealed that it is "Kurio" news app reader.
        // Based on comparison between "VIDEO::ERROR" and "VIDEO::START" on this user agent,
        // there is 0% play rate for this news reader, therefore in the meantime we will remove kurio from supported browser by videojs
        // There is also other occurences of news reader "Baca Berita" is unable to play video as well, thus same treatment
        // Pivotal: #133408721
        PlayerBrowserSupport.isNewsreaderApp()
      );
  }

  static isFirefoxMobileOnAndroid(){
    return (PlayerEnvironment.browserName() == "Firefox Mobile" && PlayerEnvironment.osName() == "Android");
  }

  static isUnsupportedFirefoxMobileOnAndroid(){
    return (PlayerEnvironment.browserName() == "Firefox Mobile" && PlayerEnvironment.osName() == "Android" && PlayerEnvironment.browserVersionMajor() < 44);
  }

  static isPalemoonOnWindowsXP(){
    return (PlayerEnvironment.browserName() == "Pale Moon (Firefox Variant)" && PlayerEnvironment.osName() == "Windows XP");
  }

  static isFirefoxOnWindowsXP(){
    return (PlayerEnvironment.browserName() == "Firefox" && PlayerEnvironment.osName() == "Windows XP");
  }

  static isOperaMini(){
    return PlayerEnvironment.browserName() == "Opera Mini";
  }

  static isHavingFlash(){
    return PlayerEnvironment.flashVersion() !== "0,0,0";
  }

  static isFirefoxWithoutMSEandFlash(){
    return PlayerEnvironment.browserName() == "Firefox" &&
      PlayerEnvironment.flashVersion() == "0,0,0" &&
      PlayerEnvironment.browserVersionMajor() < 42;
  }

  static isOperaWithoutMSEH264andFlash(){
    return PlayerEnvironment.browserName() == "Opera" &&
      PlayerEnvironment.flashVersion() == "0,0,0" &&
      PlayerEnvironment.browserVersionMajor() < 30;
  }

  static isFlashSupported(){
    return (this.isHavingFlash() && PlayerEnvironment.platform() == "web-desktop");
  }

  static isMobileAndroidBrowserVersion2X(){
    return PlayerEnvironment.platform() == "web-mobile" && PlayerEnvironment.browserName() == "Android" && PlayerEnvironment.browserVersionMajor() == 2;
  }

  static isDesktopChromeBrowserVersion6X(){
    return PlayerEnvironment.platform() == "web-desktop" && PlayerEnvironment.browserName() == "Chrome" && PlayerEnvironment.browserVersionMajor() == 6;
  }

  static preferHTML5(){
    return (PlayerEnvironment.browserName() == "Chromium" ||
        (PlayerEnvironment.browserName() == "Chrome" && PlayerEnvironment.browserVersionMajor() >= 56));
  }

  static preferFlash(){
    return PlayerEnvironment.browserName() == "IE";
  }

  static isIEMobile(){
    return PlayerEnvironment.browserName() == "IE Mobile";
  }

  static isBlackBerryWebKitOSVersionBelow10(){
    let osMajorVersion = parseInt(PlayerEnvironment.osVersionMajor());
    return PlayerEnvironment.browserName() == "BlackBerry Webkit" && osMajorVersion < 10;
  }

  static isNewsreaderApp(){
    return PlayerEnvironment.browserName() === "Kurio App" ||
    PlayerEnvironment.browserName() === "BacaBerita App";
  }
}
